/**
 * <pdp-upsell-popup>
 *
 * On PDP "add to bag", the add is deferred to this element (see
 * astound.alpine.product-add-to-cart.js). It shows Search & Discovery "related"
 * recommendations for the current product, then resolves:
 *
 *   - add an upsell from the popup -> add BOTH (main + upsell) in one cart/add
 *   - close / cross / "Not This Time" -> add ONLY the main product
 *
 * Either way it closes and opens the cart drawer. Recommendations are prefetched
 * on idle so the popup opens instantly; if there are none, the popup is skipped
 * entirely and the add behaves normally.
 *
 * Recommendation cards are the shared upsell card (via the Section Rendering
 * API, section: upsell-recommendations). Their <cart-upsell-add> buttons are
 * intercepted here (capture phase) so a click adds main + upsell together
 * instead of just the upsell.
 */
if (!customElements.get('pdp-upsell-popup')) {
  class PdpUpsellPopup extends HTMLElement {
    constructor() {
      super();
      this.intent = this.dataset.intent || 'related';
      this.productId = this.dataset.productId;
      this.limit = parseInt(this.dataset.limit, 10) || 4;
      this.fetchLimit = this.limit + 4; // extra headroom for cart-item exclusion
      this.sectionId = 'upsell-recommendations';
      this.mq = window.matchMedia('(min-width: 768px)');

      this.rawCards = null; // null = not fetched yet; [] = none
      this._cardsPromise = null;
      this.isOpen = false;
      this.resolving = false;
      this.pending = null;
      this.swiper = null;

      this.dialog = this.querySelector('.pdp-upsell__dialog');
      this.list = this.querySelector('.pdp-upsell__list');

      this._syncSwiper = this.syncSwiper.bind(this);
    }

    connectedCallback() {
      if (this.dataset.enabled !== 'true' || !this.list) return;

      // Add an upsell — intercept before <cart-upsell-add>'s own handler.
      this.list.addEventListener('click', this.onCardClick, true);
      // Dismiss (overlay / cross / "Not This Time").
      this.addEventListener('click', this.onDismiss);
      document.addEventListener('keydown', this.onKeydown);

      // Prefetch recommendations while the browser is idle.
      const prefetch = () => this.fetchCards();
      if ('requestIdleCallback' in window) {
        requestIdleCallback(prefetch, { timeout: 3000 });
      } else {
        setTimeout(prefetch, 1500);
      }
    }

    disconnectedCallback() {
      this.list?.removeEventListener('click', this.onCardClick, true);
      this.removeEventListener('click', this.onDismiss);
      document.removeEventListener('keydown', this.onKeydown);
      this.mq.removeEventListener('change', this._syncSwiper);
      if (this.swiper) this.swiper.destroy(true, true);
    }

    /* -------- public: called by product-add-to-cart -------- */
    async open(pendingMainItem) {
      if (this.isOpen || this.resolving) return;
      this.pending = pendingMainItem;

      if (this.rawCards === null) {
        // Not prefetched yet — show the shell with a loader while we fetch.
        this.showShell(true);
        await this.fetchCards();
        if (!this.isOpen) return; // dismissed during load
      }

      const html = this.buildHtml();
      if (!html) {
        // No recommendations to show -> behave like a normal add.
        return this.resolve(null);
      }

      this.list.innerHTML = html;
      if (!this.isOpen) this.showShell(false);
      else this.setLoading(false);
      this.syncSwiper();
      this.mq.addEventListener('change', this._syncSwiper);
    }

    /* -------- data -------- */
    fetchCards() {
      if (this._cardsPromise) return this._cardsPromise;

      this._cardsPromise = (async () => {
        try {
          const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          const res = await fetch(
            `${root}recommendations/products?product_id=${this.productId}` +
              `&limit=${this.fetchLimit}&intent=${this.intent}&section_id=${this.sectionId}`
          );
          if (!res.ok) {
            this.rawCards = [];
            return;
          }
          const holder = document.createElement('div');
          holder.innerHTML = await res.text();
          this.rawCards = [...holder.querySelectorAll('[data-upsell-card]')].map((el) => ({
            id: el.getAttribute('data-product-id') || '',
            html: el.outerHTML,
          }));
        } catch (e) {
          console.error('PDP upsell: fetch failed', e);
          this.rawCards = [];
        }
      })();

      return this._cardsPromise;
    }

    // Exclude the product itself and anything already in the cart.
    buildHtml() {
      if (!this.rawCards || !this.rawCards.length) return '';
      const exclude = new Set([String(this.productId), ...this.cartProductIds().map(String)]);
      return this.rawCards
        .filter((c) => !exclude.has(c.id))
        .slice(0, this.limit)
        .map((c) => c.html)
        .join('');
    }

    cartProductIds() {
      const store = window.Alpine && window.Alpine.store('cart');
      if (store && store.isLoaded && Array.isArray(store.items)) {
        return store.items.map((i) => i.product_id).filter(Boolean);
      }
      return [];
    }

    /* -------- resolve -------- */
    async resolve(upsellVariantId) {
      if (this.resolving) return;
      this.resolving = true;

      // Keep the popup open with a loader while the add is in flight, so there
      // is feedback during the request instead of a blank delay before the drawer.
      if (this.isOpen) this.setLoading(true);

      const items = [this.pending];
      if (upsellVariantId) {
        items.push({
          id: Number(upsellVariantId),
          quantity: 1,
          properties: { _source: 'pdp_upsell' },
        });
      }

      await this.commitAdd(items);

      this.pending = null;
      this.resolving = false;

      // Now close the popup and open the cart drawer.
      this.hideShell();
      const ui = window.Alpine && window.Alpine.store('ui');
      if (ui) ui.openCartDrawer();
    }

    async commitAdd(items) {
      try {
        await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items }),
        });

        const cart = window.Alpine && window.Alpine.store('cart');

        // This path adds straight to /cart/add.js rather than through the
        // store, so the one-unit tag rule is told what just went in — see
        // assets/cart-tag-limit.js. Without it the rule would have to guess
        // which of two tagged lines is the new one.
        if (cart && cart.noteJustAdded) {
          cart.noteJustAdded(items.map((item) => item.id));
        }

        await (cart && cart.refreshCart());
      } catch (e) {
        console.error('PDP upsell: add to cart failed', e);
      }
    }

    /* -------- events -------- */
    onCardClick = (e) => {
      const addEl = e.target.closest('cart-upsell-add');
      if (!addEl || !this.list.contains(addEl)) return;
      e.preventDefault();
      e.stopPropagation(); // preempt <cart-upsell-add>'s own single-add handler
      this.resolve(addEl.getAttribute('data-variant-id'));
    };

    onDismiss = (e) => {
      if (!e.target.closest('[data-pdp-upsell-dismiss]')) return;
      e.preventDefault();
      this.resolve(null);
    };

    onKeydown = (e) => {
      if (e.key === 'Escape' && this.isOpen) this.resolve(null);
    };

    /* -------- shell -------- */
    showShell(loading) {
      this.isOpen = true;
      this.setLoading(loading);
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('no-scroll');
      document.body.style.overflow = 'hidden';
    }

    hideShell() {
      this.isOpen = false;
      this.setLoading(false);
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('no-scroll');
      document.body.style.overflow = '';
      this.mq.removeEventListener('change', this._syncSwiper);
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }

    setLoading(v) {
      this.classList.toggle('is-loading', !!v);
    }

    // Carousel on all breakpoints: ~1.7 cards on mobile, 3.5 on desktop.
    // Swiper's own breakpoints handle the resize, so no destroy/recreate here.
    syncSwiper() {
      if (this.swiper) return;

      const el = this.querySelector('.pdp-upsell__swiper');
      if (!el || !window.Swiper) return;

      const modules = window.SwiperModules ? [window.SwiperModules.Pagination] : [];
      this.swiper = new window.Swiper(el, {
        modules,
        slidesPerView: 1.7,
        spaceBetween: 12,
        grabCursor: true,
        watchOverflow: true,
        pagination: {
          el: this.querySelector('.pdp-upsell__pagination'),
          clickable: true,
        },
        breakpoints: {
          768: {
            slidesPerView: 3.5,
            spaceBetween: 16,
          },
        },
      });
    }
  }

  customElements.define('pdp-upsell-popup', PdpUpsellPopup);
}
